import { zodResolver } from "@hookform/resolvers/zod";
import {
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import dayjs from "dayjs";
import {
  TaskRelatedSearchQuery,
  UserStatus,
  useTaskRelatedSearchLazyQuery,
} from "generated/graphql";
import { useCreateTask } from "hooks/tasks/useTaskMutations";
import OrganizationUsersOptions from "modules/organization/OrganizationUsersOptions/OrganizationUsersOptions";
import { useBitumioContext } from "providers/BitumioContext";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";

const schema = z.object({
  assignedUserId: z.string().min(1, "Assigned user is required."),
  content: z.string().trim().min(1, "Task description is required."),
  dueDate: z.string().optional().or(z.literal("")),
  relatedType: z.enum(["", "proposal", "job", "opportunity"]),
  relatedSelected: z
    .object({
      id: z.string(),
      label: z.string(),
      secondary: z.string().nullable().optional(),
      companyId: z.string().nullable().optional(),
      contactId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});

type RelatedType = "" | "proposal" | "job" | "opportunity";

type RelatedResult = TaskRelatedSearchQuery["taskRelatedSearch"][number];

type FormValues = z.infer<typeof schema>;

type Props = {
  open: boolean;
  onClose: () => void;
  prefillRelated?: {
    type: Exclude<RelatedType, "">;
    selected: {
      id: string;
      label: string;
      secondary?: string | null;
      companyId?: string | null;
      contactId?: string | null;
    };
  } | null;
};

const defaultValues: FormValues = {
  assignedUserId: "",
  content: "",
  dueDate: "",
  relatedType: "",
  relatedSelected: null,
};

export default function CreateTaskModal({ open, onClose, prefillRelated }: Props) {
  const [bitumio] = useBitumioContext();
  const signedInUser = bitumio.signedInUser;

  const [relatedSearch, setRelatedSearch] = useState<string>("");

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: "onChange",
  });

  const relatedType = watch("relatedType");
  const relatedSelected = watch("relatedSelected");

  useEffect(() => {
    if (!open || !prefillRelated) return;

    setValue("relatedType", prefillRelated.type, { shouldValidate: true });
    setValue("relatedSelected", prefillRelated.selected, { shouldValidate: true });
    setRelatedSearch(prefillRelated.selected.label ?? "");
  }, [open, prefillRelated, setValue]);

  useEffect(() => {
    if (!open) return;
    setValue("assignedUserId", signedInUser?.id ?? "", { shouldValidate: true });
  }, [open, setValue, signedInUser?.id]);

  const [createTask, { loading }] = useCreateTask();

  const [runRelatedSearch, { data: relatedData, loading: relatedLoading }] = useTaskRelatedSearchLazyQuery(
    {
      fetchPolicy: "no-cache",
    }
  );

  const relatedOptions: RelatedResult[] = useMemo(() => {
    return relatedData?.taskRelatedSearch ?? [];
  }, [relatedData]);

  const resetForm = () => {
    reset(defaultValues);
    setRelatedSearch("");
  };

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    const hasRelated = Boolean(values.relatedType) && Boolean(values.relatedSelected?.id);

    const dueAtIso = values.dueDate
      ? dayjs(values.dueDate).endOf("day").format("YYYY-MM-DD HH:mm:ss")
      : null;

    try {
      await createTask({
        variables: {
          input: {
            assignedUserId: values.assignedUserId,
            content: values.content.trim(),
            dueAt: dueAtIso,
            related: hasRelated
              ? {
                  type: values.relatedType,
                  id: values.relatedSelected!.id,
                }
              : null,
          },
        },
      });

      resetForm();
      onClose();
    } catch {
      return;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ component: "form", onSubmit: handleSubmit(onSubmit) }}
    >
      <DialogTitle>Create Task</DialogTitle>

      <DialogContent>
        <MDBox mt={1}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="assignedUserId"
                control={control}
                render={({ field }) => (
                  <OrganizationUsersOptions
                    queryOverride={{ first: 200, status: UserStatus.ACTIVE }}
                    selected={
                      signedInUser?.id
                        ? {
                            label:
                              signedInUser.name ||
                              `${signedInUser.firstName ?? ""} ${signedInUser.lastName ?? ""}`.trim() ||
                              signedInUser.email ||
                              "Me",
                            value: signedInUser.id,
                            hasEmployee: signedInUser.hasEmployee ?? false,
                          }
                        : undefined
                    }
                  >
                    {({ organizationUsersOptions, loading: usersLoading }) => (
                      <TextField
                        label="Assigned To"
                        select
                        fullWidth
                        size="small"
                        value={field.value}
                        onChange={field.onChange}
                        error={!!errors.assignedUserId}
                        helperText={errors.assignedUserId?.message}
                        disabled={usersLoading}
                        sx={{
                          "& .MuiInputBase-root": {
                            minHeight: 40,
                          },
                          "& .MuiSelect-select": {
                            display: "flex",
                            alignItems: "center",
                            minHeight: 40,
                            paddingTop: "10px",
                            paddingBottom: "10px",
                            paddingLeft: "12px",
                          },
                        }}
                      >
                        {organizationUsersOptions.map((option) => (
                          <MenuItem key={String(option.value)} value={String(option.value)}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </OrganizationUsersOptions>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Task"
                fullWidth
                multiline
                minRows={3}
                placeholder="What needs to get done?"
                error={!!errors.content}
                helperText={errors.content?.message}
                {...register("content")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Due date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional"
                size="small"
                {...register("dueDate")}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <MDTypography variant="button" fontWeight="medium" display="block">
                Related to (optional)
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                Link this task to a Proposal, Job, or Opportunity.
              </MDTypography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="relatedType"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Type"
                    select
                    value={field.value}
                    onChange={(e) => {
                      const nextType = e.target.value as RelatedType;
                      field.onChange(nextType);
                      setRelatedSearch("");
                      setValue("relatedSelected", null, { shouldValidate: true });
                    }}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiInputBase-root": {
                        minHeight: 40,
                      },
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        minHeight: 40,
                        paddingTop: "10px",
                        paddingBottom: "10px",
                      },
                    }}
                  >
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="proposal">Proposal</MenuItem>
                    <MenuItem value="job">Job</MenuItem>
                    <MenuItem value="opportunity">Opportunity</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="relatedSelected"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    disabled={!relatedType}
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                    options={relatedOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    loading={relatedLoading}
                    inputValue={relatedSearch}
                    onInputChange={(_, value) => {
                      setRelatedSearch(value);

                      if (!relatedType) return;
                      if (!value || value.trim().length < 2) return;

                      runRelatedSearch({
                        variables: {
                          type: relatedType,
                          search: value.trim(),
                          first: 10,
                        },
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={relatedType ? "Search" : "Search (choose a type)"}
                        placeholder={"Search by ID" ? "Search by ID" : ""}
                        fullWidth
                        size="small"
                        sx={{
                          "& .MuiInputBase-root": { minHeight: 40 },
                          "& input": { paddingTop: "10px", paddingBottom: "10px" },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {relatedLoading ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span>{option.label}</span>
                          {option.secondary ? (
                            <span style={{ fontSize: 12, opacity: 0.7 }}>{option.secondary}</span>
                          ) : null}
                        </div>
                      </li>
                    )}
                  />
                )}
              />
            </Grid>

            {relatedType &&
            relatedSearch.trim().length > 0 &&
            relatedSearch.trim().length < 2 &&
            !relatedSelected ? (
              <Grid item xs={12}>
                <MDTypography variant="caption" color="text">
                  Type at least 2 characters to search.
                </MDTypography>
              </Grid>
            ) : null}

            {relatedType &&
            relatedSearch.trim().length >= 2 &&
            !relatedLoading &&
            relatedOptions.length === 0 &&
            !relatedSelected ? (
              <Grid item xs={12}>
                <MDTypography variant="caption" color="text">
                  No matches found.
                </MDTypography>
              </Grid>
            ) : null}
          </Grid>
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color="success" type="submit" disabled={loading || !isValid}>
          Add Task
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
